import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as React from 'react';
import { cn } from '../lib/utils.js';

/**
 * A determinate meter: how much of a bounded thing is gone.
 *
 * Radix rather than a pair of divs because a bar drawn with divs is invisible
 * to a screen reader -- it carries no role and no value, so the only way to
 * learn the number is to find it repeated in the surrounding copy. This one
 * announces itself, and `value` also lands in the DOM as `aria-valuenow`,
 * which is what makes a meter assertable in a test without reading inline
 * styles.
 *
 * Brand-colored fill on a muted track, matching the three hand-rolled copies
 * this replaces (Billing's allowance strip, Usage's allowance hero, and the
 * Spec usage popover) rather than shadcn's default `bg-primary`.
 */
const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
    /** Replaces the brand fill, for a meter that should read as used up. */
    indicatorClassName?: string;
  }
>(({ className, indicatorClassName, value, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn('relative h-2 w-full overflow-hidden rounded-full bg-muted', className)}
    value={value}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className={cn(
        'h-full w-full flex-1 rounded-full bg-brand transition-transform',
        indicatorClassName,
      )}
      style={{ transform: `translateX(-${100 - (value ?? 0)}%)` }}
    />
  </ProgressPrimitive.Root>
));
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
